import type {SessionIdToLogsMap, LogEntry, Session} from '../types/session'
import {useCAPIQuery} from './useCAPIQuery'
import {fetchCAPI} from '../utils/fetch-capi'
import {isValidLogEntry} from '../types/session'
import {pullInactiveLogsApiPath, taskInactiveLogsApiPath} from '../utils/api-url-helpers'

interface UseInactiveLogsProps {
  sessions: Session[]
  /** Optional pull ID. If provided, uses the batch PR endpoint. If not, fetches per-session. */
  pullId?: number
  taskId?: string
  repoOwner?: string
  repoName?: string
}

/**
 * Fetches logs for inactive (completed/failed/cancelled) sessions.
 *
 * When a pullId is provided, uses the batch endpoint `/agents/sessions/resource/pull/{pullId}/inactive_logs`.
 * When no pullId is provided, fetches logs for each inactive session individually.
 */
export function useInactiveLogs({sessions, pullId, taskId, repoOwner, repoName}: UseInactiveLogsProps) {
  // Original query key logic: track non-inactive sessions so we refetch when one becomes inactive
  const inactiveSessionIds = sessions
    .filter(session => ['completed', 'failed', 'cancelled'].includes(session.state.toLowerCase()))
    .map(s => s.id)
    .sort()

  return useCAPIQuery<SessionIdToLogsMap>({
    queryKey: ['agent-inactive-session-logs', taskId, pullId, repoOwner, repoName, ...inactiveSessionIds],
    queryFn: async () => {
      let logsPath = undefined
      if (taskId && repoOwner && repoName) {
        logsPath = taskInactiveLogsApiPath(repoOwner, repoName, taskId)
      } else if (pullId) {
        logsPath = pullInactiveLogsApiPath(pullId)
      }

      // If we have a path, use the original batch endpoint
      if (logsPath) {
        const result = await fetchCAPI({
          path: logsPath,
        })
        if (!result.ok) {
          throw new Error(`Error fetching inactive session logs: ${result.statusText}`)
        }
        const data = await result.json()
        const logsMap = data.logs
        const parsedLogsMap: SessionIdToLogsMap = {}
        for (const sessionId in logsMap) {
          const logString = logsMap[sessionId]

          const logs: LogEntry[] = []
          const formattedLogs = formatLogs(logString)
          for (const log of formattedLogs) {
            if (isValidLogEntry(log)) {
              logs.push(log)
            } else {
              throw new Error(`Invalid log entry received: ${JSON.stringify(log)}`)
            }

            parsedLogsMap[sessionId] = logs
          }
        }
        return parsedLogsMap
      }

      // No pullId or taskId: fetch logs for each inactive session individually
      // TODO: add route for batch fetching by session IDs or by task ID
      return fetchInactiveLogsBySessionIds(inactiveSessionIds)
    },
    enabled: !!pullId || !!taskId || inactiveSessionIds.length > 0,
    // Keep showing previous data while new data is loading
    placeholderData: prev => prev,
  })
}

/**
 * Fetches logs for each session individually using the streaming endpoint.
 * Used when no PR is available (PR-less tasks).
 */
async function fetchInactiveLogsBySessionIds(sessionIds: string[]): Promise<SessionIdToLogsMap> {
  const parsedLogsMap: SessionIdToLogsMap = {}

  // Fetch logs for each session in parallel
  const logPromises = sessionIds.map(async sessionId => {
    try {
      const logs = await fetchLogsForSession(sessionId)
      return {sessionId, logs}
    } catch (error) {
      // Log error but don't fail the entire batch
      // eslint-disable-next-line no-console
      console.error(`Error fetching logs for session ${sessionId}:`, error)
      return {sessionId, logs: []}
    }
  })

  const results = await Promise.all(logPromises)
  for (const {sessionId, logs} of results) {
    parsedLogsMap[sessionId] = logs
  }

  return parsedLogsMap
}

/**
 * Fetches logs for a single session.
 */
async function fetchLogsForSession(sessionId: string): Promise<LogEntry[]> {
  const result = await fetchCAPI({
    path: `/agents/sessions/${sessionId}/logs`,
  })

  if (!result.ok) {
    throw new Error(`Error fetching logs for session ${sessionId}: ${result.statusText}`)
  }

  const logString = await result.text()
  const logs: LogEntry[] = []
  const formattedLogs = formatLogs(logString)

  for (const log of formattedLogs) {
    if (isValidLogEntry(log)) {
      logs.push(log)
    } else {
      throw new Error(`Invalid log entry received: ${JSON.stringify(log)}`)
    }
  }

  return logs
}

function formatLogs(logs: string): LogEntry[] {
  const jsonStrings = parseSSEString(logs)
  return jsonStrings.map(jsonString => JSON.parse(jsonString))
}

function parseSSEString(sseString: string): string[] {
  return sseString
    .split('\n\n') // Split by double newlines (event boundaries)
    .map(event => event.trim())
    .filter(event => event.length > 0)
    .map(event => {
      const lines = event.split('\n')
      const dataLines = lines.filter(line => line.startsWith('data: ')).map(line => line.substring(6)) // Remove "data: " prefix
      return dataLines.join('\n')
    })
    .filter(data => data !== '[DONE]') // Exclude [DONE] messages
    .filter(data => data.length > 0)
}
